import { useQuery } from '@apollo/client'
import type { ListActionResult, ListQueryResult, QueryInput } from '../types/QueryInput'
import type { ListActionOptions } from '../useListAction/types'
import { useBuildListQueryOptions } from './useBuildListQueryOptions'
import { useBuildListQueryResults } from './useBuildListQueryResults'
import { getOperationName } from '@apollo/client/utilities'
import { useEffect } from 'react'
import { buildSubscriptionQueryFromQuery } from '@ztechapps/common.utils'

export type UseListProps<T = any> = QueryInput<T> & {
  listActionOptions?: ListActionOptions
  subscribeToUpdates?: boolean
}

export type UseListResult<T = any> = ListQueryResult<T> & ListActionResult

/**
 * Use this to query graphql using the defined query
 * @param query             - the graphql query (required)
 * @param entity            - the entity we are querying (required)
 * @param projection        - how we select data on the database for performance optimization
 * @param sort              - how we sort the list
 * @param limit             - if the limit null or zero, it mean it should get all records
 * @param skip              - the number of documents to be skip, if not defined/null will be set as zero
 * @param filter            - the filter condition on how we query the data
 * @param options           - should contain some options for apollo-related query or dbOptions
 * @param listActionOptions - pass the options if we are using the results from listActions
 * @param subscribeToUpdates - if true, will subscribe to onCreate, onUpdate, and onDelete events
 */
export const useList = <T = any>({
  query,
  entity,
  projection,
  sort,
  limit,
  skip,
  filter,
  options = {},
  listActionOptions = {},
  subscribeToUpdates = false,
}: UseListProps<T>): UseListResult<T> => {
  //entity can be derived from the operations automatically
  if (!entity) {
    entity = getOperationName(query)
  }

  //prepare for the query options here
  const { queryOptions, listActionResults, dbOptions } = useBuildListQueryOptions({
    query,
    entity,
    projection,
    sort,
    limit,
    skip,
    filter,
    options,
    listActionOptions,
  })

  //execute the query here
  const queryResult = useQuery(query, queryOptions)

  const { subscribeToMore } = queryResult

  useEffect(() => {
    if (!subscribeToUpdates) return

    console.log('[useList] Setting up subscriptions for entity:', entity)

    // Subscribe to created events
    const { subscriptionQuery: createdQuery, subscriptionName: createdName } = buildSubscriptionQueryFromQuery(
      query,
      'created'
    )
    console.log('[useList] Created subscription name:', createdName)
    console.log('[useList] Created subscription query:', createdQuery)

    const unsubscribeCreated = subscribeToMore({
      document: createdQuery,
      variables: { filter },
      updateQuery: (prev, { subscriptionData }) => {
        console.log('[useList] Created event received:', subscriptionData)
        if (!subscriptionData?.data) return prev

        const newItem = subscriptionData.data[createdName]
        const existingList = prev[entity] || []

        // Check if item already exists in the list
        const itemExists = existingList.some((item: any) => item._id === newItem._id)
        if (itemExists) {
          console.log('[useList] Item already exists, skipping:', newItem._id)
          return prev
        }

        console.log('[useList] Adding new item to list:', newItem)

        // Get meta field name and increment count
        const metaFieldName = `${entity}Meta`
        const currentCount = prev[metaFieldName]?.count || 0
        const newCount = currentCount + 1
        console.log('[useList] Incrementing count from', currentCount, 'to', newCount)

        // Add new item to the beginning of the list and increment count
        return {
          ...prev,
          [entity]: [newItem, ...existingList],
          [metaFieldName]: {
            ...prev[metaFieldName],
            count: newCount,
          },
        }
      },
    })

    // Subscribe to updated events
    const { subscriptionQuery: updatedQuery, subscriptionName: updatedName } = buildSubscriptionQueryFromQuery(
      query,
      'updated'
    )
    console.log('[useList] Updated subscription name:', updatedName)

    const unsubscribeUpdated = subscribeToMore({
      document: updatedQuery,
      variables: { filter },
      updateQuery: (prev, { subscriptionData }) => {
        console.log('[useList] Updated event received:', subscriptionData)
        if (!subscriptionData?.data) return prev

        const updatedItem = subscriptionData.data[updatedName]
        const existingList = prev[entity] || []

        console.log('[useList] Updating item in list:', updatedItem)
        // Update the item in the list
        const updatedList = existingList.map((item: any) => (item._id === updatedItem._id ? updatedItem : item))

        return {
          ...prev,
          [entity]: updatedList,
        }
      },
    })

    // Subscribe to deleted events
    const { subscriptionQuery: deletedQuery, subscriptionName: deletedName } = buildSubscriptionQueryFromQuery(
      query,
      'deleted'
    )
    console.log('[useList] Deleted subscription name:', deletedName)

    const unsubscribeDeleted = subscribeToMore({
      document: deletedQuery,
      variables: { filter },
      updateQuery: (prev, { subscriptionData }) => {
        console.log('[useList] Deleted event received:', subscriptionData)
        if (!subscriptionData?.data) return prev

        const deletedItem = subscriptionData.data[deletedName]
        const existingList = prev[entity] || []

        console.log('[useList] Removing item from list:', deletedItem)
        // Remove the item from the list
        const filteredList = existingList.filter((item: any) => item._id !== deletedItem._id)

        // Get meta field name and decrement count
        const metaFieldName = `${entity}Meta`
        const currentCount = prev[metaFieldName]?.count || 0
        const newCount = Math.max(0, currentCount - 1) // Ensure count doesn't go below 0
        console.log('[useList] Decrementing count from', currentCount, 'to', newCount)

        // Remove item from list and decrement count
        return {
          ...prev,
          [entity]: filteredList,
          [metaFieldName]: {
            ...prev[metaFieldName],
            count: newCount,
          },
        }
      },
    })

    console.log('[useList] All subscriptions set up successfully')

    // Cleanup subscriptions on unmount
    return () => {
      console.log('[useList] Cleaning up subscriptions for entity:', entity)
      unsubscribeCreated()
      unsubscribeUpdated()
      unsubscribeDeleted()
    }
  }, [subscribeToUpdates, subscribeToMore, entity, filter, query])

  //parse the result
  return useBuildListQueryResults<T>({
    queryResult,
    entity,
    limit,
    dbOptions,
    listActionResults,
  })
}
